import React from 'react';
import { useTranslation } from 'next-i18next';
import clsx from 'clsx';
import ProductImage from '@components/atoms/vc-images/components/product-image';
import ProductLikeButton from '@components/atoms/product-like-button/product-like-button';
import Link from '@components/atoms/link/link';
import { getCountryNameFromCountryISOCode } from '@helpers/utils/general';
import ProductSchema from '@components/structured-schemas/product-schema';
import { ImagesService } from '@services/image-service';
import useLocalizationQuery from '@hooks/localization/use-localization';
import dynamic from 'next/dynamic';
import useActiveDealQuery from '@hooks/deal/use-active-deal-query';
import { useProductClickTracking } from '@hooks/product-card/use-product-click-tracking';
import { useProductCardTags } from '@hooks/product-card/use-product-card-tags';
import { ProductCardProps } from '@components/organisms/catalog/components/product-card/product-card-props';
import useUser from '@hooks/user/use-user';
import ProductCardDutiesTag from '@components/organisms/catalog/components/product-card/product-card-duties-tag';
import { AriaLabelBuilder } from '@helpers/product-card/aria-label-builder';
import styles from './product-card.module.scss';

const ProductCardLocationIcon = dynamic(
  () => import('@components/molecules/products/product-card-location-icon/product-card-location-icon'),
);

const DEFAULT_DISPLAY_OPTIONS = {
  discountPrice: true,
  productLocation: true,
  productSize: true,
  productTags: true,
};

const ProductCard: React.FC<ProductCardProps> = (props) => {
  const {
    product,
    index,
    customClass,
    pageType = 'personalized',
    moduleType,
    displayElements,
    disableLazyLoad = false,
    trackingAlerts,
    productFeedCategory,
    isNewProduct = false,
    onClick,
    containerId,
    forceImgLazyLoadingCb,
    includeProductSchema = true,
    fetchpriority = 'low',
    isGoodPriceVariantEnabled = false,
    parentUid,
    parentPosition,
    disableLikeButton = false,
    onScrollPositionSet,
    renderFooter = null,
    isClientSideRoutingEnabled = false,
  } = props;

  const displayElementOptions = {
    ...DEFAULT_DISPLAY_OPTIONS,
    ...displayElements,
  };

  const { t } = useTranslation();
  const { t: tA11y } = useTranslation('a11y');
  const { countryList } = useLocalizationQuery();
  const { activeDeal } = useActiveDealQuery();

  const { user } = useUser();

  // TCRM-3965: Check if the user is in the same region as the product (To align with favorites payload)
  const isSameRegion =
    product.localCountries?.some((country) => country.toLowerCase() === user?.countryISO.toLowerCase()) ?? false;
  const isLocalCountry: boolean =
    product?.flags?.includes('local-country') ||
    isSameRegion || // used by Favourite cards
    product?.isLocal; // used by Recently viewed cards
  const productUrl = product.path;
  const shouldDisplaySize: boolean = displayElementOptions.productSize && !!product?.size?.size;

  const { cmsProps } = props;

  const { productClickTracking, trackProductCrossSellClick } = useProductClickTracking({
    product,
    pageType,
    moduleType,
    parentPosition,
    index,
    parentUid,
    trackingAlerts,
    isNewProduct,
    productFeedCategory,
    cmsProps,
  });

  const { tags, shouldShowProductTags, getTagName } = useProductCardTags({
    product,
    showProductTags: displayElementOptions.productTags,
  });

  const handleClick = async (): Promise<void> => {
    trackProductCrossSellClick();
    productClickTracking();
    onScrollPositionSet?.(`${pageType}__productCard__${product.id}`);
    onClick?.(product, index);
  };

  const getSEOImgPath = (path: string): string => {
    return ImagesService.improveSEOProductPathImg(path, product.path);
  };

  const generateProductCardAriaLabel = (): string => {
    const ariaLabelBuilder = new AriaLabelBuilder({
      product,
      shouldDisplaySize,
      shouldShowProductTags,
      isNewProduct,
      tags,
      getTagName,
      displayElementOptions,
      countryList,
      tA11y,
    });

    return ariaLabelBuilder.generateAriaLabel();
  };

  return (
    <Link
      href={productUrl}
      id={containerId}
      onClick={handleClick}
      className={clsx(styles.productCard, customClass)}
      data-cy={`${pageType}__productCard__${product.id}`}
      data-vc-dd-action-name={`product_card__${pageType}`}
      aria-label={generateProductCardAriaLabel()}
      enableClientSideNavigation={isClientSideRoutingEnabled}
    >
      {includeProductSchema && <ProductSchema product={product} />}

      {/* Product image */}
      <div
        className={styles.productCard__imageContainer}
        data-cy="productCard__imageContainer"
      >
        <div
          className={clsx(styles.productCard__image, {
            [styles.sold]: product.sold,
          })}
          data-cy="productCard__image"
        >
          <ProductImage
            src={getSEOImgPath(product.pictures[0]?.path)}
            height={256}
            width={256}
            alt="" // empty on purpose as the aria-label is set on the Link
            type="snippet"
            disableLazyLoad={disableLazyLoad}
            forceImgLazyLoadingCb={forceImgLazyLoadingCb}
            fetchPriority={fetchpriority}
          />
          {product.sold && (
            <span className={styles.productCard__image__soldText}>{t('PRODUCT_SNIPPET.SOLD_CARD')}</span>
          )}
        </div>
      </div>

      {/* Tags */}
      {shouldShowProductTags && (
        <div
          className={styles.productCard__tagsContainer}
          data-cy="productCard__tagsContainer"
        >
          {tags.map((tag) => (
            <span
              style={
                tag == 'dealEligible'
                  ? { color: activeDeal.textColor, background: activeDeal.backgroundColor }
                  : { background: '#f5f6f8' }
              }
              className={styles.productCard__tag}
              key={tag}
              data-cy={`product_card_tag_${tag}`}
            >
              {getTagName(tag)}
            </span>
          ))}
        </div>
      )}
      {isNewProduct && <div className={styles['productCard__text--new']}>{t('ALERTS.LANDING.NEW_LABEL')}</div>}

      {/* Product details */}
      <div
        className={clsx(
          styles.productCard__productDetails,
          !shouldShowProductTags && !isNewProduct && styles['productCard__productDetails--noProductTags'],
        )}
        data-cy="productCard__productDetails__noProductTags"
      >
        <h3
          className={styles.productCard__productLink}
          data-cy="productCard__productLink"
        >
          <span
            className={clsx(styles.productCard__text, styles['productCard__text--brand'])}
            data-cy="productCard__text__brand"
          >
            {product.brand.localizedName?.toUpperCase()}
          </span>

          <span
            className={clsx(styles.productCard__text, styles['productCard__text--name'])}
            data-cy="productCard__text__name"
          >
            {product.name}
          </span>
          {shouldDisplaySize && (
            <p
              className={clsx(styles['productCard__text--size'])}
              data-cy="productCard__text__size"
            >
              <span className="vc-d-none">{t('PRODUCT.DESCRIPTION.DETAILS.SIZE')}</span>
              <span>
                {product.size.size}&nbsp;
                {product.size.localizedStandard}
              </span>
            </p>
          )}
        </h3>
        {!disableLikeButton && (
          <ProductLikeButton
            customClass={styles.productCard__productDetails__likeButton}
            product={product}
            position={index + 1}
            alertTracking={{
              alertTitle: trackingAlerts?.title,
              alertStatus: isNewProduct ? 'new' : '',
            }}
            pageType={pageType}
            parentPosition={parentPosition}
            {...{ moduleType, productFeedCategory }}
          />
        )}
        <div
          className={clsx(styles['productCard__text--price'], {
            [styles['productCard__text--price--discount']]:
              product?.regularPrice?.formatted && displayElementOptions.discountPrice,
            [styles['productCard__text--price--discount--goodPriceVariant']]:
              product?.regularPrice?.formatted && displayElementOptions.discountPrice && isGoodPriceVariantEnabled,
          })}
        >
          {product?.regularPrice?.formatted && (
            <span
              className={clsx({
                [styles['productCard__text--price--regularPrice']]: displayElementOptions.discountPrice,
                [styles['productCard__text--price--regularPrice--goodPriceVariant']]:
                  displayElementOptions.discountPrice && isGoodPriceVariantEnabled,
              })}
              data-cy="productCard__text__price__regularPrice"
            >
              {product?.regularPrice?.formatted}
            </span>
          )}
          <div>
            {product?.price.formatted && (
              <span
                className={clsx(styles.productCard__text)}
                data-cy="productCard__text__price__discount"
              >
                {product?.price.formatted}
                {isGoodPriceVariantEnabled && !!product.discountPercentage && product.discountPercentage >= 20 && (
                  <span className={styles.productCard__discountPercentage}>{`-${product.discountPercentage}%`}</span>
                )}
              </span>
            )}
            {product.hasNewTaxes && <ProductCardDutiesTag showTooltip={pageType === 'catalog'} />}
          </div>
        </div>

        {displayElementOptions.productLocation && getCountryNameFromCountryISOCode(countryList, product.isoCountry) && (
          <div
            className={clsx(styles.productCard__text__bottom, 'vc-text-s')}
            data-cy="productCard__text__location"
          >
            <ProductCardLocationIcon isLocalCountry={isLocalCountry} />
            <span className={styles.productCard__text__bottom__location}>
              {getCountryNameFromCountryISOCode(countryList, product.isoCountry)}
            </span>
          </div>
        )}
      </div>

      {/* Render optional footer (FavoriteProductCard) */}
      {renderFooter}
    </Link>
  );
};

export default ProductCard;
